import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller that tracks Turbo Stream render events.
 * Adds data-turbo-update-count and data-turbo-last-update attributes
 * to elements that receive Turbo Stream updates.
 *
 * Usage: Add data-controller="turbo-tracking" to any element you want to track.
 * The controller will automatically increment counters when the element receives
 * a Turbo Stream update.
 *
 * @example
 * <div data-controller="turbo-tracking" id="user_count_question_1">
 *   <%= render partial: "live_reporting/user_count", locals: { question: question } %>
 * </div>
 *
 * After a Turbo Stream update, the element will have:
 * data-turbo-update-count="1"
 * data-turbo-last-update="2025-01-15T10:30:00.000Z"
 */
export default class extends Controller {
  connect() {
    this.updateCount = parseInt(this.element.dataset.turboUpdateCount || "0", 10);
    this.handleAfterRenderBound = this.handleAfterRender.bind(this);
    document.addEventListener("turbo:after-stream-render", this.handleAfterRenderBound);
  }

  disconnect() {
    document.removeEventListener("turbo:after-stream-render", this.handleAfterRenderBound);
  }

  /**
   * Called after a Turbo Stream render completes.
   * Checks if this element was the target and updates tracking attributes.
   */
  handleAfterRender(event) {
    const streamElement = event.target;
    const targetId = streamElement.getAttribute("target");

    // Check if this element was the target of the update
    if (this.element.id === targetId || this.element.contains(document.getElementById(targetId))) {
      this.updateCount++;
      this.element.dataset.turboUpdateCount = this.updateCount.toString();
      this.element.dataset.turboLastUpdate = new Date().toISOString();

      // Dispatch a custom event for additional tracking
      this.element.dispatchEvent(new CustomEvent("turbo:tracked-update", {
        bubbles: true,
        detail: {
          count: this.updateCount,
          targetId: targetId,
          action: streamElement.getAttribute("action"),
          timestamp: this.element.dataset.turboLastUpdate
        }
      }));
    }
  }
}
